import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  ChevronDown, 
  ChevronUp,
  Filter, 
  X
} from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Contract, ContractGroup } from "@/lib/types";
import { TranslationText } from "@/components/i18n/TranslationText";
import { useTranslationEdit } from "@/contexts/TranslationEditContext";

// Define the type for sortable columns - update to include expiryStatus
export type SortableColumn = 
  | "supplierName" 
  | "importance" 
  | "expiryDate" 
  | "signedDate" 
  | "expectedCost" 
  | "type"
  | "category"
  | "expiryStatus"
  | "group";

// Define the type for sort direction
export type SortDirection = "asc" | "desc";

interface ContractTableHeaderProps {
  selectedColumns: string[];
  isColumnSelected: (id: string) => boolean;
  sortColumn: SortableColumn;
  sortDirection: SortDirection;
  toggleSort: (column: SortableColumn) => void;
  toggleAllSelection: () => void;
  toggleFilter: (name: string, value: string | number) => void;
  sortedContracts: any[];
  selectedContracts: Set<string>;
  filters: Record<string, Set<string | number>>;
  importanceBadges: any;
  uniqueTypes: any[];
  uniqueCategories: any[];
  contractGroups: ContractGroup[];
  className?: string;
}

export function ContractTableHeader({
  selectedColumns,
  isColumnSelected,
  sortColumn,
  sortDirection,
  toggleSort,
  toggleAllSelection,
  toggleFilter,
  sortedContracts,
  selectedContracts,
  filters,
  importanceBadges,
  uniqueTypes,
  uniqueCategories,
  contractGroups,
  className
}: ContractTableHeaderProps) {
  const { t } = useTranslation();
  const { isEditMode } = useTranslationEdit();

  // Function to render column header with sort controls
  const renderSortableHeader = (labelKey: string, column: SortableColumn, width?: string) => {
    const isSorted = sortColumn === column;
    const style = width ? { width } : {};
    
    return (
      <div 
        className="flex items-center cursor-pointer"
        style={style}
        onClick={() => toggleSort(column)}
      >
        <TranslationText i18nKey={labelKey}>
          {t(labelKey)}
        </TranslationText>
        {isSorted && (
          <span className="ml-1">
            {sortDirection === "asc" ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        )}
      </div>
    );
  };

  // Function to render filter badge and menu
  const renderFilterMenu = (columnName: string, items: any[], valueKey: string = "name") => {
    const hasFilter = filters[columnName]?.size > 0;
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`h-6 w-6 p-0 ml-1 ${hasFilter ? "text-primary" : "text-muted-foreground"}`}
          >
            <Filter className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
          <DropdownMenuGroup>
            {items.map((item) => {
              // For importance, use the numeric value (the index + 1)
              let value;
              let label;
              
              if (columnName === 'importance') {
                // Make sure we're working with numbers for importance
                if (typeof item.value === 'number') {
                  value = item.value;
                  label = item.label;
                } else {
                  // Fallback if value isn't set properly
                  const index = Object.values(importanceBadges).indexOf(item);
                  value = index + 1;
                  label = item.label || `Priority ${value}`;
                }
              } else {
                value = item[valueKey] || item;
                label = value;
              }
              
              // Ensure we have a valid value to use as a key and for filtering
              const keyValue = value || `unknown-${Math.random()}`;
              const isSelected = filters[columnName]?.has(value);
              
              return (
                <DropdownMenuItem 
                  key={`${columnName}-${keyValue}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFilter(columnName, value);
                  }}
                  className="flex items-center justify-between"
                >
                  <span>{label}</span>
                  {isSelected && <X className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <TableHeader className={className}>
      <TableRow className="bg-gray-50/50 dark:bg-gray-900/50">
        {isColumnSelected("checkbox") && (
          <TableHead className="w-10">
            <Checkbox 
              checked={sortedContracts.length > 0 && selectedContracts.size === sortedContracts.length}
              onClick={toggleAllSelection}
              aria-label="Select all"
            />
          </TableHead>
        )}
        
        {isColumnSelected("supplierName") && (
          <TableHead>
            <div className="flex items-center">
              {renderSortableHeader("supplier", "supplierName")}
            </div>
          </TableHead>
        )}
        
        {isColumnSelected("importance") && (
          <TableHead className="w-28">
            <div className="flex items-center">
              {renderSortableHeader("importance", "importance")}
              {renderFilterMenu("importance", Object.values(importanceBadges), "value")}
            </div>
          </TableHead>
        )}
        
        {isColumnSelected("expiryStatus") && (
          <TableHead className="w-24">
            <div className="flex items-center">
              <TranslationText i18nKey="status">
                {t('status')}
              </TranslationText>
              {renderFilterMenu("expiryStatus", [
                "Active", 
                "Expiring Soon", 
                "Expired"
              ])}
            </div>
          </TableHead>
        )}
        
        {isColumnSelected("type") && (
          <TableHead>
            <div className="flex items-center">
              {renderSortableHeader("type", "type")}
              {renderFilterMenu("type", uniqueTypes)}
            </div>
          </TableHead>
        )}
        
        {isColumnSelected("category") && (
          <TableHead>
            <div className="flex items-center">
              {renderSortableHeader("category", "category")}
              {renderFilterMenu("category", uniqueCategories)}
            </div>
          </TableHead>
        )}
        
        {isColumnSelected("expiryDate") && (
          <TableHead>
            {renderSortableHeader("expiryDate", "expiryDate")}
          </TableHead>
        )}
        
        {isColumnSelected("signedDate") && (
          <TableHead>
            {renderSortableHeader("signedDate", "signedDate")}
          </TableHead>
        )}
        
        {isColumnSelected("expectedCost") && (
          <TableHead className="text-right">
            {renderSortableHeader("value", "expectedCost")}
          </TableHead>
        )}
        
        {isColumnSelected("currency") && (
          <TableHead>
            <TranslationText i18nKey="currency">
              {t('currency')}
            </TranslationText>
          </TableHead>
        )}
        
        {isColumnSelected("actions") && (
          <TableHead className="w-20 text-right">
            <TranslationText i18nKey="actions">
              {t('actions')}
            </TranslationText>
          </TableHead>
        )}
      </TableRow>
    </TableHeader>
  );
}
